import { CloudCustomerAddress } from "@omni/types/api/customer/cloud/address.model";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Alert } from "@omni/ui/components/Alert/alert.component";
import { Button } from "@omni/ui/components/Button/button.component";
import classnames from "classnames";
import { useState } from "react";

import DefaultAddresses from "@/components/Account/Profile/ProfileAddressBook/DefaultAddresses/defaultAddresses.component";
import OtherAddresses from "@/components/Account/Profile/ProfileAddressBook/OtherAddresses/otherAddresses.component";
import { CreateAddressModal } from "@/components/Address/modals/CreateAddressModal/createAddressModal.component";
import { Divider } from "@/components/General/Divider/divider.component";
import { Col } from "@/components/General/Grid/col.component";
import { Container } from "@/components/General/Grid/container.component";
import { Row } from "@/components/General/Grid/row.component";
import { Spinner } from "@/components/General/Spinner/spinner.component";
import { TError } from "@/redux/modules/value/errors/errors.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type AddressBookProps = {
    error: Array<TError> | undefined;
    loading: boolean;
    updateDefaultBilling: (address: CloudCustomerAddress) => Promise<void>;
    updateDefaultDelivery: (address: CloudCustomerAddress) => Promise<void>;
    addresses: Array<CloudCustomerAddress>;
    createAddress: (
        addressing: string | null | undefined,
        firstName: string,
        lastName: string,
        country: string,
        city: string,
        street: string,
        box: string | null | undefined,
        number: string,
        zipCode: string,
        validationStatus: AddressValidationStatus
    ) => Promise<void>;
    updateAddress: (address: CloudCustomerAddress) => Promise<void>;
};

const AddressBook = ({
    loading,
    error,
    updateDefaultDelivery,
    updateDefaultBilling,
    addresses = [],
    createAddress,
    updateAddress
}: AddressBookProps) => {
    const { t } = useGranite();
    const defaultPaymentAddress = addresses?.find(
        (address) => address.defaultPaymentAddress
    );
    const defaultDeliveryAddress = addresses?.find(
        (address) => address.defaultDeliveryAddress
    );
    const otherAddresses = addresses.filter(
        (address) =>
            !address.defaultDeliveryAddress && !address.defaultPaymentAddress
    );
    const canDelete = addresses.length > 1;

    const [isNewAddressModalOpen, setIsNewAddressModalOpen] = useState(false);

    return (
        <>
            <CreateAddressModal
                isOpen={isNewAddressModalOpen}
                createAddress={createAddress}
                toggleHandler={setIsNewAddressModalOpen}
            />
            {error &&
                !loading &&
                error.map((err, index) => (
                    <Alert
                        key={index}
                        color="danger"
                        closeAriaLabel={t("general.alert.close")}
                    >
                        {err.message}
                    </Alert>
                ))}
            {loading &&
                (!otherAddresses ||
                    !defaultDeliveryAddress ||
                    !defaultPaymentAddress) && <Spinner />}
            {!loading &&
                (!otherAddresses ||
                    (!defaultDeliveryAddress && !defaultPaymentAddress)) && (
                    <div>{t("general.profile.addressbook.noAddresses")}</div>
                )}
            {(!loading ||
                otherAddresses ||
                defaultDeliveryAddress ||
                defaultPaymentAddress) && (
                <Container>
                    <DefaultAddresses
                        defaultDeliveryAddress={defaultDeliveryAddress}
                        defaultPaymentAddress={defaultPaymentAddress}
                        updateDefaultDelivery={updateDefaultDelivery}
                        updateDefaultBilling={updateDefaultBilling}
                        canDelete={canDelete}
                        updateAddress={updateAddress}
                    />
                    <OtherAddresses
                        addresses={otherAddresses}
                        updateDefaultBilling={updateDefaultBilling}
                        updateDefaultDelivery={updateDefaultDelivery}
                        canDelete={canDelete}
                        updateAddress={updateAddress}
                    />
                    <Row>
                        <Col>
                            <Divider className={classnames("mb-3")} />
                            <Button
                                data-test="profileAddressBook-button-add"
                                color="link"
                                type="button"
                                onClick={() => setIsNewAddressModalOpen(true)}
                            >
                                <i className="las la-plus mr-2" />
                                {t("address.action.add")}
                            </Button>
                        </Col>
                    </Row>
                </Container>
            )}
        </>
    );
};

export default AddressBook;
